import className from 'classnames';
import React, { useEffect } from 'react';
import { MdRemoveCircle } from 'react-icons/md';
import { Button, ConfirmDialog, Label, PlaceholderButton, ScrollArea } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import getColCellAlignment from '../Core/getColCellAlignment';
import getColWidth from '../Core/getColWidth';
import './HorizontalGrid.scss';
import { handleKeyDownWithGridTabbing } from './shared';
import { IHorizontalGridProps } from './types';

/**
 * This implementation follows the API for the Handsontable Grid API. Try to keep them consistent for ease of use.
 * https://handsontable.com/docs/6.2.2/Options.html
 */
const HorizontalGridBase: React.FC<IHorizontalGridProps> = ({
  center,
  colHeaders,
  columns,
  colWidths,
  colCellAlignments,
  cornerHeader,
  data,
  disabled,
  hiddenRows,
  onCreateCol,
  onReceivedData,
  onRemoveCol,
  removeConfirm = { confirm: false },
  rowHeaders,
  rowHeaderWidth,
  scrollLinkId,
  tabDirection = 'vertical',
  rows,
}) => {
  // Inform parent of data if it wants to do something with the grid data.
  useEffect(() => onReceivedData && data && onReceivedData(data, hiddenRows), [onReceivedData, data, hiddenRows]);

  /*
   * Row / col helpers
   */
  const showColumnHeaders = colHeaders && colHeaders.length > 0;
  const showRowHeaders = rowHeaders && rowHeaders.length > 0;
  // Corner headers only supported when both row and column headers appear
  // If you need a grid with a corner header in another situation, you will need to "fill in" empty label components
  // for rows/columns as required.
  const showCornerHeader = showColumnHeaders && showRowHeaders;
  const isHiddenRow = (rowIndex: any) => !!(hiddenRows && hiddenRows.includes(rowIndex));
  const showRemoveColRow = !disabled && onRemoveCol && !columns?.every((c) => c.preventRemoveCol);

  /*
   * All widths / heights are done in JS so we can set them based on properties set by the parent
   */
  const gridHeaderColWidth = rowHeaderWidth === 'auto' ? undefined : rowHeaderWidth ? rowHeaderWidth : 200;
  const gridColMarginRight = 5;
  const gridCellHeight = 34;
  const gridCellMarginBottom = 5;

  let gridColPlaceholderMarginTop = showRemoveColRow ? gridCellHeight + gridCellMarginBottom : 0;
  gridColPlaceholderMarginTop += colHeaders && colHeaders.length > 0 ? gridCellHeight + gridCellMarginBottom : 0;

  const gridColPlaceholderMarginBottom = gridCellMarginBottom;

  const gridHeaderStyle = { height: gridCellHeight, marginBottom: gridCellMarginBottom };

  const getGridCellStyle = (colIndex: any) => ({
    height: gridCellHeight,
    marginBottom: gridCellMarginBottom,
    justifyContent: getColCellAlignment(colCellAlignments, colIndex),
  });
  /*
   * Custom key handling to move around the grid.
   * Create React references for every input that is not null
   */
  const focusRefs = data?.map((col) => col?.map((row) => row && React.createRef<HTMLElement>()));

  return (
    <div className={className('HGrid', { 'HGrid-center': center })}>
      {(showRowHeaders || showCornerHeader) && (
        <div className="HGrid-HeaderCol" style={{ width: gridHeaderColWidth }}>
          {showRemoveColRow && <div className="HGrid-Cell HGrid-Cell-header" style={gridHeaderStyle} />}

          {showCornerHeader && (
            <Label className="HGrid-Cell HGrid-Cell-rowHeader" style={gridHeaderStyle}>
              {cornerHeader}
            </Label>
          )}

          {showRowHeaders &&
            rowHeaders?.map(
              (rowHeader: any, rowIndex: any) =>
                !isHiddenRow(rowIndex) && (
                  <Label
                    className="HGrid-Cell HGrid-Cell-rowHeader"
                    style={gridHeaderStyle}
                    key={rows?.[rowIndex]?.key ?? rowIndex}
                  >
                    {rowHeader}
                  </Label>
                )
            )}
        </div>
      )}

      <ScrollArea className="HGrid-Viewport" scrollLinkId={scrollLinkId}>
        <div className="HGrid-Cols">
          {data?.map((col, colIndex) => (
            <div
              className={className('HGrid-Col', {
                'HGrid-Col-dimmed': columns && columns[colIndex] && columns[colIndex].dim,
              })}
              key={columns?.[colIndex]?.key ?? colIndex}
              style={{ marginRight: gridColMarginRight, width: getColWidth(colWidths, colIndex) }}
            >
              {showRemoveColRow && (
                <div className="HGrid-Cell" style={gridHeaderStyle}>
                  {!columns?.[colIndex]?.preventRemoveCol && (
                    <ConfirmDialog {...removeConfirm}>
                      <Button name={`${colIndex}`} subtle error onClick={() => onRemoveCol?.(colIndex)}>
                        <MdRemoveCircle size="2em" />
                      </Button>
                    </ConfirmDialog>
                  )}
                </div>
              )}

              {showColumnHeaders && (
                <Label className="HGrid-Cell HGrid-Cell-colHeader" style={gridHeaderStyle}>
                  {colHeaders?.[colIndex]}
                </Label>
              )}

              {col?.map(
                (row, rowIndex) =>
                  !isHiddenRow(rowIndex) && (
                    <div
                      className="HGrid-Cell"
                      style={getGridCellStyle(colIndex)}
                      key={rows?.[rowIndex]?.key ?? rowIndex}
                    >
                      {row &&
                        React.cloneElement(row, {
                          inGrid: true,
                          truncate: true,
                          disabled: row.props?.disabled || disabled,
                          onKeyDown: handleKeyDownWithGridTabbing(
                            row.props.onKeyDown,
                            tabDirection === 'horizontal',
                            focusRefs,
                            focusRefs?.[colIndex]?.[rowIndex]
                          ),
                          focusRef: focusRefs?.[colIndex]?.[rowIndex],
                        })}
                    </div>
                  )
              )}
            </div>
          ))}

          {onCreateCol && !disabled && (
            <div
              className="HGrid-Col HGrid-Col-placeholder"
              style={{
                width: getColWidth(colWidths, data?.length ?? 0),
                marginTop: gridColPlaceholderMarginTop,
                marginBottom: gridColPlaceholderMarginBottom,
              }}
            >
              <PlaceholderButton onClick={onCreateCol} />
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

const HorizontalGrid = withInjectedProps(HorizontalGridBase);

export { HorizontalGrid };
export default HorizontalGrid;
